import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';
import { adminSessionUsable } from '../../../utils/authAdmin';
import { ensureAdminWikiImageCookies } from '../../../utils/wikiImageCookies';
import WikiTreeView from '../../../components/WikiTreeView';
import WikiPageEditor from '../../../components/WikiPageEditor';
import StaleContentBanner from '../../../components/StaleContentBanner';
import {
    FaPlus,
    FaBook,
    FaSpinner,
    FaCheck,
    FaPencilAlt,
    FaTimes,
    FaExclamationTriangle,
    FaSearch
} from 'react-icons/fa';

const STORAGE_KEY = 'wikiProjectsTabState';

/** Debounced at the tab, as every other live surface is (LIV-13). */
const LIVE_REFRESH_DEBOUNCE_MS = 500;

const WikiProjectsTab = ({ searchQuery, onUnsavedChanges, liveMessages = [] }) => {
    // Load initial state from localStorage
    const getInitialState = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (err) {
            console.error('Failed to load wiki projects tab state:', err);
        }
        return { selectedProjectId: null, selectedPageId: null };
    };

    const initialState = getInitialState();

    // Projects list state
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Project selector state
    const [projectSearchTerm, setProjectSearchTerm] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);
    const [pendingProjectId, setPendingProjectId] = useState(initialState.selectedProjectId);
    const [showProjectDropdown, setShowProjectDropdown] = useState(false);

    // Pages state
    const [pages, setPages] = useState([]);
    const [loadingPages, setLoadingPages] = useState(false);
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [pendingPageId, setPendingPageId] = useState(initialState.selectedPageId);
    const [selectedPage, setSelectedPage] = useState(null);

    // Editor state
    const [pageContent, setPageContent] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
    const [lastSavedContent, setLastSavedContent] = useState('');
    const saveTimeoutRef = useRef(null);

    // Create page modal state
    const [showCreatePageModal, setShowCreatePageModal] = useState(false);
    const [createPageParentId, setCreatePageParentId] = useState(null);
    const [newPageTitle, setNewPageTitle] = useState('');
    const [creating, setCreating] = useState(false);

    // Delete confirmation state
    const [showDeletePageConfirm, setShowDeletePageConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Publish state
    const [publishing, setPublishing] = useState(false);

    const dropdownRef = useRef(null);

    // Determine if content has unsaved changes - MUST be defined before useEffects that depend on it
    const hasUnsavedChanges = pageContent !== lastSavedContent;

    // Fetch projects (leads with status 'won' and projectId)
    // ⚠️ SILENT MODE (LIV, D1) - the message path raises no banner and keeps
    // what is on screen if the request fails.
    const fetchProjects = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setError(null);
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads`);
            if (response.ok) {
                const data = await response.json();
                if (!data.leads) {
                    throw new Error('API response missing leads array');
                }
                // Filter for "won" status leads with a projectId (these are actual projects)
                const projectLeads = data.leads.filter(
                    lead => lead.status === 'won' && lead.projectId
                );
                // Map to project format - company and name are required fields, use explicit check
                const projectList = projectLeads.map(lead => {
                    const projectName = lead.company ? lead.company : (lead.name ? lead.name : 'Unnamed Project');
                    return {
                        projectId: lead.projectId,
                        name: projectName,
                        leadId: lead.id,
                        email: lead.email
                    };
                });
                // Sort by name
                projectList.sort((a, b) => a.name.localeCompare(b.name));
                setProjects(projectList);
                setFilteredProjects(projectList);
            } else if (!silent) {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to fetch projects');
            } else {
                console.warn('Background project refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch projects:', err);
            if (!silent) setError('Failed to fetch projects');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Fetch pages for selected project
    const fetchPages = useCallback(async (projectId, { silent = false, skipIfDirty = false } = {}) => {
        if (!projectId) return;
        if (!silent) setLoadingPages(true);
        try {
            // Signed cookies for this project's images, scoped to
            // /wiki-images/<projectId>/, before any page content renders.
            // Without them every image 403s, so stop rather than show a broken page.
            if (!await ensureAdminWikiImageCookies(projectId)) {
                if (!silent) {
                    setPages([]);
                    setError('Failed to authorise wiki images');
                }
                return;
            }

            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-projects/${projectId}/pages`);
            if (response.ok) {
                const data = await response.json();
                if (!Array.isArray(data)) {
                    throw new Error('API response is not an array');
                }
                // ⛔ RE-CHECK ON ARRIVAL. The dirty test that let this request go
                // out happened before the round trip; anything typed since would
                // be reset by the effect that watches `pages`.
                if (skipIfDirty && liveStateRef.current.hasUnsavedChanges) return;
                setPages(data);
            }
        } catch (err) {
            console.error('Failed to fetch pages:', err);
        } finally {
            if (!silent) setLoadingPages(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // Restore selected project from pending state once projects are loaded
    useEffect(() => {
        if (pendingProjectId && projects.length > 0 && !selectedProject) {
            const project = projects.find(p => p.projectId === pendingProjectId);
            if (project) {
                setSelectedProject(project);
                setProjectSearchTerm(project.name);
            }
            setPendingProjectId(null);
        }
    }, [pendingProjectId, projects, selectedProject]);

    // Restore selected page from pending state once pages are loaded
    useEffect(() => {
        if (pendingPageId && pages.length > 0 && !selectedPageId) {
            const page = pages.find(p => p.pageId === pendingPageId);
            if (page) {
                setSelectedPageId(pendingPageId);
            }
            setPendingPageId(null);
        }
    }, [pendingPageId, pages, selectedPageId]);

    // Persist state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                selectedProjectId: selectedProject?.projectId,
                selectedPageId: selectedPageId
            }));
        } catch (err) {
            console.error('Failed to save wiki projects tab state:', err);
        }
    }, [selectedProject, selectedPageId]);

    // Report unsaved changes to parent
    useEffect(() => {
        if (onUnsavedChanges) {
            onUnsavedChanges(hasUnsavedChanges);
        }
    }, [hasUnsavedChanges, onUnsavedChanges]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProjectDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter projects based on search term
    useEffect(() => {
        if (!projectSearchTerm) {
            setFilteredProjects(projects);
        } else {
            const term = projectSearchTerm.toLowerCase();
            setFilteredProjects(
                projects.filter(p =>
                    p.name.toLowerCase().includes(term) ||
                    p.email.toLowerCase().includes(term)
                )
            );
        }
    }, [projectSearchTerm, projects]);

    // When project selection changes
    useEffect(() => {
        if (selectedProject) {
            fetchPages(selectedProject.projectId);
            setSelectedPageId(null);
            setSelectedPage(null);
            setPageContent('');
            setLastSavedContent('');
            setSaveStatus('idle');
        } else {
            setPages([]);
            setSelectedPageId(null);
            setSelectedPage(null);
        }
    }, [selectedProject, fetchPages]);

    // When page selection changes
    useEffect(() => {
        if (selectedPageId) {
            const page = pages.find(p => p.pageId === selectedPageId);
            setSelectedPage(page);
            // Load draft content (what admin edits) - empty string is valid for new pages
            const content = page && page.draftHtml ? page.draftHtml : '';
            setPageContent(content);
            setLastSavedContent(content);
            setSaveStatus('idle');
        } else {
            setSelectedPage(null);
            setPageContent('');
            setLastSavedContent('');
            setSaveStatus('idle');
        }
    }, [selectedPageId, pages]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-13, D9). WikiEditor.js handles the socket and forwards
    // the message; this tab owns the lists and the open page.
    //
    // ⚠️ A project page is the ONE client-wiki record that also reaches the
    // client portal: the stream handler leaves it on the generic path AND adds
    // the admin signal, so `project_updated` still wakes pages/Client/Wiki.js.
    // -----------------------------------------------------------------------

    /** "Somebody else saved this page" - D9. */
    const [stalePage, setStalePage] = useState(false);
    useEffect(() => {
        if (!hasUnsavedChanges) setStalePage(false);
    }, [hasUnsavedChanges]);

    // GATE 3: the DISCRETE actions hold the whole refresh.
    //
    // ⛔ `saveStatus === 'saving'` is deliberately NOT one of them, unlike the
    // Templates tab's `savingPage`. This tab AUTOSAVES: `handleContentChange`
    // sets 'saving' on the first keystroke and holds it through a 1.5s debounce,
    // so gating on it would defer every message for as long as somebody is
    // typing - and by the time the autosave landed the page would be clean
    // again, which is the one state in which the banner never shows. D9's
    // banner would have been unreachable on this tab.
    const liveRefreshBlocked = Boolean(creating || deleting || publishing);
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => { liveRefreshBlockedRef.current = liveRefreshBlocked; }, [liveRefreshBlocked]);

    const liveStateRef = useRef({});
    liveStateRef.current = { selectedProject, selectedPage, hasUnsavedChanges };

    // ⛔ TWO KINDS HELD SEPARATELY, NOT ONE SLOT. Holding the last message only
    // drops every message before it - and this tab can be held for up to the
    // 500ms debounce, or for as long as a create/delete/publish runs. A message
    // for another project arriving after one for the OPEN project would have
    // lost the open page's refresh outright.
    //
    // ⛔ THE PROJECT LIST RIDES `leads_updated`, NEVER `wiki_updated` (Bill,
    // 2026-09-03, option B of Fable's round 2). The list is `GET /admin/leads` -
    // the leads SCAN plus one AdminGetUser per won lead - and it renders name,
    // email and company, none of which a page write can change; while the
    // page editor AUTOSAVES 1.5s after every pause. Refetching it per page
    // save, as the plan first said, drove that fan-out from every open Client
    // Wiki tab every couple of seconds. A lead WON is what changes this list,
    // and that is the leads table's own signal.
    const pendingRef = useRef({ projects: false, projectIds: new Set() });
    const refreshTimerRef = useRef(null);

    const hasPending = () => pendingRef.current.projects || pendingRef.current.projectIds.size > 0;

    const runLiveRefresh = useCallback(async () => {
        // GATE 0 lives HERE - at the point of refetch, once per coalesced
        // refresh - not per message in the parent's socket handler (Fable, round
        // 2). Checked BEFORE the pending set is taken, so a session that cannot
        // be renewed leaves it held for the next signal (D11); a background
        // request must never take apiAdmin's 401 path and redirect a tab with an
        // editor open in it.
        if (!await adminSessionUsable()) return;
        const pending = pendingRef.current;
        pendingRef.current = { projects: false, projectIds: new Set() };
        if (!pending.projects && pending.projectIds.size === 0) return;

        const { selectedProject: project, selectedPage: page, hasUnsavedChanges: dirty }
            = liveStateRef.current;

        if (pending.projects) fetchProjects({ silent: true });
        if (!project || !pending.projectIds.has(project.projectId)) return;

        if (!dirty) {
            // ⚠️ `skipIfDirty`: the response lands later, and applying it would
            // reset the editor through the [selectedPageId, pages] effect -
            // losing anything typed between this check and the response. The
            // trigger is the echo of the user's OWN save, so it recurs.
            fetchPages(project.projectId, { silent: true, skipIfDirty: true });
            return;
        }

        // ⛔ DIRTY: refetching the pages would replace `pages`, and the effect
        // watching it resets `pageContent` - destroying what is being typed. So
        // read WITHOUT setting state and compare `updatedAt`, which is the only
        // monotonic field these rows carry.
        if (!page) return;
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${project.projectId}/pages`);
            if (!response.ok) return;
            const data = await response.json();
            const fresh = Array.isArray(data) ? data.find(p => p.pageId === page.pageId) : null;
            if (fresh && page.updatedAt && fresh.updatedAt > page.updatedAt) {
                setStalePage(true);
            }
        } catch (err) {
            console.error('Failed to check for a newer page:', err);
        }
    }, [fetchProjects, fetchPages]);

    const scheduleLiveRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            runLiveRefresh();
        }, LIVE_REFRESH_DEBOUNCE_MS);
    }, [runLiveRefresh]);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    // Everything above the last sequence this tab saw - React batches, so a
    // single-message prop would collapse a burst into its last entry.
    //
    // ⚠️ SEEDED FROM THE QUEUE AT MOUNT, not from zero. The parent keeps up to
    // fifty entries and unmounts the inactive tab, so a tab switch would replay
    // every queued message: one more leads scan plus a Cognito call per won
    // lead, half a second after this tab's own mount fetch loaded the same data
    // (Fable, round 2). What arrived before the mount is what the mount fetched.
    const lastSeqRef = useRef(liveMessages.length ? liveMessages[liveMessages.length - 1].seq : 0);
    useEffect(() => {
        const fresh = liveMessages.filter(entry => entry.seq > lastSeqRef.current);
        if (fresh.length === 0) return;
        lastSeqRef.current = fresh[fresh.length - 1].seq;

        let queued = false;
        for (const { message } of fresh) {
            if (message.type === 'leads_updated') {
                pendingRef.current.projects = true;
                queued = true;
            } else if (message.type === 'wiki_updated' && message.scope === 'project') {
                pendingRef.current.projectIds.add(message.projectId);
                queued = true;
            }
        }
        if (!queued || liveRefreshBlockedRef.current) return;   // D11: held, not dropped
        scheduleLiveRefresh();
    }, [liveMessages, scheduleLiveRefresh]);

    useEffect(() => {
        if (liveRefreshBlocked || !hasPending()) return;
        scheduleLiveRefresh();
    }, [liveRefreshBlocked, scheduleLiveRefresh]);

    const handleStaleReload = useCallback(() => {
        const { selectedProject: project } = liveStateRef.current;
        setStalePage(false);
        if (project) fetchPages(project.projectId);
    }, [fetchPages]);

    // Filter pages by global search query
    const filteredPages = pages.filter(page => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return page.title.toLowerCase().includes(query) ||
               (page.draftHtml && page.draftHtml.toLowerCase().includes(query)) ||
               (page.contentHtml && page.contentHtml.toLowerCase().includes(query));
    });

    // Determine if current page is a draft
    const isDraft = selectedPage && (
        !selectedPage.contentHtml ||
        selectedPage.contentHtml !== selectedPage.draftHtml
    );

    // Select project from dropdown
    const handleSelectProject = (project) => {
        if (hasUnsavedChanges) {
            if (!window.confirm('You have unsaved changes. Discard them?')) {
                return;
            }
        }
        setSelectedProject(project);
        setProjectSearchTerm(project.name);
        setShowProjectDropdown(false);
    };

    // Handle page selection with unsaved changes warning
    const handlePageSelect = useCallback((pageId) => {
        if (hasUnsavedChanges && pageId !== selectedPageId) {
            if (!window.confirm('You have unsaved changes. Discard them?')) {
                return;
            }
        }
        setSelectedPageId(pageId);
    }, [hasUnsavedChanges, selectedPageId]);

    // Handle content change with autosave
    const handleContentChange = useCallback((html) => {
        setPageContent(html);

        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Content matches what is stored - no autosave, no status flicker. Guards
        // against the editor re-serialising content it has just loaded.
        if (html === lastSavedContent) return;

        // Don't autosave if no page selected
        if (!selectedProject || !selectedPage) return;

        // Set saving status immediately to show user we'll save
        setSaveStatus('saving');

        // Debounce the actual save (1.5 seconds)
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages/${selectedPage.pageId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ draftHtml: html })
                    }
                );
                if (response.ok) {
                    setLastSavedContent(html);
                    setSaveStatus('saved');
                    // ⛔ MERGE THE RESPONSE, never a BROWSER timestamp (D9).
                    // The route re-reads and returns the written row, so the
                    // SERVER's `updatedAt` is in this body - and that is the
                    // value the echo check compares against. A minted one is
                    // this machine's clock, which is not the clock the stream
                    // record was written by, so the page's own autosave would
                    // come back looking like somebody else's write.
                    const saved = await response.json().catch(() => null);
                    setPages(prev => prev.map(p =>
                        p.pageId === selectedPage.pageId
                            ? { ...p, ...(saved && saved.pageId ? saved : {}), draftHtml: html }
                            : p
                    ));
                    // Reset to idle after 2 seconds
                    setTimeout(() => setSaveStatus('idle'), 2000);
                } else {
                    setSaveStatus('error');
                }
            } catch (err) {
                console.error('Autosave failed:', err);
                setSaveStatus('error');
            }
        }, 1500);
    }, [selectedProject, selectedPage, lastSavedContent]);

    // Cleanup save timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // Publish page
    const handlePublish = async () => {
        if (!selectedProject || !selectedPage || publishing) return;

        // If there are unsaved changes, save first
        if (hasUnsavedChanges) {
            // Clear pending autosave
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Save immediately
            setSaveStatus('saving');
            try {
                const saveResponse = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages/${selectedPage.pageId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ draftHtml: pageContent })
                    }
                );
                if (!saveResponse.ok) {
                    setError('Failed to save before publishing');
                    setSaveStatus('error');
                    return;
                }
                setLastSavedContent(pageContent);
            } catch (err) {
                console.error('Save before publish failed:', err);
                setError('Failed to save before publishing');
                setSaveStatus('error');
                return;
            }
        }

        setPublishing(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages/${selectedPage.pageId}/publish`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            if (response.ok) {
                const updatedPage = await response.json();
                // Update local state
                setPages(prev => prev.map(p =>
                    p.pageId === selectedPage.pageId ? updatedPage : p
                ));
                setSelectedPage(updatedPage);
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to publish page');
            }
        } catch (err) {
            console.error('Publish failed:', err);
            setError('Failed to publish page');
        } finally {
            setPublishing(false);
        }
    };

    // Create new page
    const handleCreatePage = async (e) => {
        e.preventDefault();
        if (!selectedProject || !newPageTitle.trim() || creating) return;

        setCreating(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: newPageTitle.trim(),
                        parentId: createPageParentId,
                        draftHtml: ''
                    })
                }
            );
            if (response.ok) {
                const data = await response.json();
                setShowCreatePageModal(false);
                setNewPageTitle('');
                setCreatePageParentId(null);
                await fetchPages(selectedProject.projectId);
                setSelectedPageId(data.pageId);
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to create page');
            }
        } catch (err) {
            console.error('Failed to create page:', err);
            setError('Failed to create page');
        } finally {
            setCreating(false);
        }
    };

    // Handle add child from tree context menu
    const handleAddChild = (parentId) => {
        setCreatePageParentId(parentId);
        setShowCreatePageModal(true);
    };

    // Handle delete page request from tree context menu
    const handleDeletePageRequest = (pageId) => {
        const page = pages.find(p => p.pageId === pageId);
        if (page) {
            setShowDeletePageConfirm(page);
        }
    };

    // Delete page
    const handleDeletePage = async () => {
        if (!selectedProject || !showDeletePageConfirm || deleting) return;

        setDeleting(true);
        setDeleteError(null);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages/${showDeletePageConfirm.pageId}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setShowDeletePageConfirm(null);
                if (selectedPageId === showDeletePageConfirm.pageId) {
                    setSelectedPageId(null);
                }
                fetchPages(selectedProject.projectId);
            } else {
                const data = await response.json();
                setDeleteError(data.message ? data.message : 'Failed to delete page');
            }
        } catch (err) {
            console.error('Failed to delete page:', err);
            setDeleteError('Failed to delete page');
        } finally {
            setDeleting(false);
        }
    };

    // Handle page reorder from tree drag-drop
    const handleReorderPage = async (pageId, newParentId, newOrder) => {
        if (!selectedProject) return;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-projects/${selectedProject.projectId}/pages/${pageId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ parentId: newParentId, order: newOrder })
                }
            );
            if (response.ok) {
                fetchPages(selectedProject.projectId);
            }
        } catch (err) {
            console.error('Failed to reorder page:', err);
        }
    };

    // Handle image upload
    const handleImageUpload = async (file) => {
        console.log('WikiProjectsTab.handleImageUpload called with file:', file?.name, file?.type);
        console.log('selectedProject:', selectedProject?.projectId, selectedProject?.name);

        if (!selectedProject) {
            console.error('handleImageUpload: No project selected');
            return null;
        }

        try {
            console.log('Requesting presigned URL...');
            // Get presigned URL
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-images/upload-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contextId: selectedProject.projectId,
                    filename: file.name,
                    contentType: file.type
                })
            });

            console.log('Presigned URL response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to get presigned URL:', errorText);
                throw new Error('Failed to get upload URL');
            }

            const { uploadUrl, imageUrl } = await response.json();
            console.log('Got presigned URL, imageUrl:', imageUrl);

            // Upload to S3
            console.log('Uploading to S3...');
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type }
            });

            console.log('S3 upload response status:', uploadResponse.status);
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('S3 upload failed:', errorText);
                throw new Error('Failed to upload image');
            }

            // The editor inserts this src immediately, so make sure a live
            // cookie covers it - the one from page load may be near expiry on a
            // long editing session. Throwing here stops a broken image being
            // inserted; the catch below surfaces it.
            if (!await ensureAdminWikiImageCookies(selectedProject.projectId)) {
                throw new Error('Failed to authorise wiki images');
            }

            console.log('Image upload successful, returning:', imageUrl);
            return imageUrl;
        } catch (err) {
            console.error('Image upload failed:', err);
            setError('Failed to upload image');
            return null;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
                <FaSpinner className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
        );
    }

    // Render save status indicator
    const renderSaveStatus = () => {
        switch (saveStatus) {
            case 'saving':
                return (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        <FaSpinner className="w-3 h-3 animate-spin" />
                        Saving...
                    </span>
                );
            case 'saved':
                return (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                        <FaCheck className="w-3 h-3" />
                        Saved
                    </span>
                );
            case 'error':
                return (
                    <span className="flex items-center gap-1 text-xs text-red-600">
                        <FaExclamationTriangle className="w-3 h-3" />
                        Save failed
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div className="h-full flex bg-gray-50">
            {/* Left Panel - Project Selector and Tree */}
            <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                {/* Project Selector */}
                <div className="p-4 border-b border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Select Project
                    </label>
                    <div className="border border-gray-200 rounded-lg overflow-y-auto max-h-48">
                        {projects.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500">
                                No projects found
                            </div>
                        ) : (
                            projects.map(project => (
                                <button
                                    key={project.projectId}
                                    type="button"
                                    onClick={() => handleSelectProject(project)}
                                    className={`
                                        w-full px-4 py-2 text-left text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0
                                        ${selectedProject?.projectId === project.projectId ? 'bg-primary-100 text-primary-900' : ''}
                                    `}
                                >
                                    <div className="font-medium text-gray-900">{project.name}</div>
                                    <div className="text-xs text-gray-500">{project.email}</div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Pages Tree */}
                {selectedProject ? (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-4 pb-2 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-700">Pages</h3>
                            <button
                                onClick={() => {
                                    setCreatePageParentId(null);
                                    setShowCreatePageModal(true);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                                <FaPlus className="w-2.5 h-2.5" />
                                New Page
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-2 pb-4">
                            {loadingPages ? (
                                <div className="flex justify-center py-4">
                                    <FaSpinner className="w-5 h-5 text-gray-400 animate-spin" />
                                </div>
                            ) : (
                                <WikiTreeView
                                    pages={filteredPages}
                                    selectedPageId={selectedPageId}
                                    onSelect={handlePageSelect}
                                    onReorder={handleReorderPage}
                                    onAddChild={handleAddChild}
                                    onDelete={handleDeletePageRequest}
                                    showDraftIndicators={true}
                                    storageKey={`wikiProjectTree_${selectedProject.projectId}`}
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center p-4">
                        <p className="text-sm text-gray-500 text-center">
                            Select a project to view and edit its wiki pages
                        </p>
                    </div>
                )}
            </div>

            {/* Right Panel - Page Editor */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
                {!selectedProject ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <FaBook className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600">Select a Project</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                Choose a project from the dropdown to edit its wiki pages.
                            </p>
                        </div>
                    </div>
                ) : !selectedPage ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <FaBook className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">
                                Select a page to edit or create a new one.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Page Header */}
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div>
                                    <h3 className="font-semibold text-gray-900">{selectedPage.title}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        {isDraft ? (
                                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                                <FaPencilAlt className="w-3 h-3" />
                                                Draft
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-xs text-green-600">
                                                <FaCheck className="w-3 h-3" />
                                                Published
                                            </span>
                                        )}
                                        {renderSaveStatus()}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {isDraft && (
                                    <button
                                        onClick={handlePublish}
                                        disabled={publishing}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
                                    >
                                        {publishing ? (
                                            <FaSpinner className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <FaCheck className="w-4 h-4" />
                                        )}
                                        Publish
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {/* D9: saved elsewhere while open and dirty. The
                                typed text stays exactly as it is. */}
                            {stalePage && (
                                <StaleContentBanner
                                    message="This page was saved somewhere else. Your changes are still here."
                                    onReload={handleStaleReload}
                                    confirm="You have unsaved changes. Discard them and load the newer page?"
                                    busy={saveStatus === 'saving'}
                                />
                            )}
                            <WikiPageEditor
                                content={pageContent}
                                title={selectedPage.title}
                                onChange={handleContentChange}
                                onImageUpload={handleImageUpload}
                                debounceMs={0}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Error Toast */}
            {error && (
                <div className="fixed bottom-4 right-4 bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
                    <FaExclamationTriangle className="w-5 h-5" />
                    <span className="text-sm">{error}</span>
                    <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Create Page Modal */}
            {showCreatePageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">
                                {createPageParentId ? 'Create Child Page' : 'Create Page'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCreatePageModal(false);
                                    setNewPageTitle('');
                                    setCreatePageParentId(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePage} className="p-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Page Title
                                </label>
                                <input
                                    type="text"
                                    value={newPageTitle}
                                    onChange={(e) => setNewPageTitle(e.target.value)}
                                    placeholder="e.g., Getting Started"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                />
                            </div>
                            <p className="text-xs text-gray-500 mb-4">
                                New pages start as drafts. Edit and publish when ready for clients to see.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreatePageModal(false);
                                        setNewPageTitle('');
                                        setCreatePageParentId(null);
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!newPageTitle.trim() || creating}
                                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {creating && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Page Confirmation Modal */}
            {showDeletePageConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Delete Page</h3>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to delete <strong>{showDeletePageConfirm.title}</strong>?
                                {showDeletePageConfirm.contentHtml && (
                                    <span className="block mt-2 text-orange-600">
                                        This page has published content visible to clients.
                                    </span>
                                )}
                            </p>
                            {deleteError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <FaExclamationTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                                        <p className="text-sm text-red-700">{deleteError}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeletePageConfirm(null);
                                        setDeleteError(null);
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeletePage}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {deleting && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WikiProjectsTab;
